import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Plus, Trash2, GripVertical, Save, BookOpen,
  FileText, Video, Link as LinkIcon, HelpCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = '';

function makeId(prefix) {
  return `${prefix}_${Math.random().toString(36).substring(2, 14)}`;
}

export default function CourseBuilderPage({ user }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!courseId;
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  const [form, setForm] = useState({
    title: '',
    intro: '',
    description: '',
    level: 'Beginner',
    topic_tags: '',
    price: '49',
    max_participants: '',
    pass_mark: '70',
    cpd_credits: '0',
    delivery_mode: 'online',
    coming_soon: false,
    trainer_name: '',
    venue: '',
    course_dates: '',
    sessions: [],
    what_you_will_learn: '',
  });

  const [modules, setModules] = useState([
    { module_id: makeId('mod'), title: '', duration: '', content: '', video_url: '', resources: [] }
  ]);

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/courses');
      return;
    }
    if (isEditing) {
      loadCourse();
    }
  }, [courseId, user]);

  const loadCourse = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/courses/${courseId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load course');
      const data = await res.json();
      setForm({
        title: data.title || '',
        intro: data.intro || '',
        description: data.description || '',
        level: data.level || 'Beginner',
        topic_tags: (data.topic_tags || []).join(', '),
        price: String(data.price ?? 49),
        max_participants: data.max_participants ?? '',
        pass_mark: String(data.pass_mark || 70),
        cpd_credits: String(data.cpd_credits || 0),
        delivery_mode: data.delivery_mode || 'online',
        coming_soon: data.coming_soon || false,
        trainer_name: data.trainer_name || '',
        venue: data.venue || '',
        course_dates: data.course_dates || '',
        sessions: (data.sessions || []).map(s => ({
          session_id: s.session_id || makeId('sess'),
          date: s.date || '',
          start_time: s.start_time || '',
          end_time: s.end_time || '',
          venue: s.venue || '',
          capacity: s.capacity ?? '',
          notes: s.notes || '',
        })),
        what_you_will_learn: data.what_you_will_learn || '',
      });
      if (data.modules?.length > 0) {
        setModules(data.modules.map(m => ({
          module_id: m.module_id || makeId('mod'),
          title: m.title || '',
          duration: m.duration || '',
          content: m.content || '',
          video_url: m.video_url || '',
          resources: m.resources || [],
        })));
      }
      if (data.exam?.questions?.length > 0) {
        setQuestions(data.exam.questions.map(q => ({
          question_id: q.question_id || makeId('q'),
          question: q.question || '',
          options: q.options || ['', '', '', ''],
          correct_answer: q.correct_answer ?? 0,
          points: q.points || 10,
        })));
      }
    } catch (e) {
      toast.error('Failed to load course');
    }
  };

  const updateModule = (idx, field, value) => {
    setModules(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const addModule = () => {
    setModules(prev => [...prev, { module_id: makeId('mod'), title: '', duration: '', content: '', video_url: '', resources: [] }]);
  };

  const removeModule = (idx) => {
    if (modules.length <= 1) { toast.error('Need at least one module'); return; }
    setModules(prev => prev.filter((_, i) => i !== idx));
  };

  const addResource = (modIdx) => {
    setModules(prev => prev.map((m, i) =>
      i === modIdx ? { ...m, resources: [...m.resources, { title: '', url: '' }] } : m
    ));
  };

  const updateResource = (modIdx, resIdx, field, value) => {
    setModules(prev => prev.map((m, i) =>
      i === modIdx ? {
        ...m,
        resources: m.resources.map((r, ri) => ri === resIdx ? { ...r, [field]: value } : r)
      } : m
    ));
  };

  const removeResource = (modIdx, resIdx) => {
    setModules(prev => prev.map((m, i) =>
      i === modIdx ? { ...m, resources: m.resources.filter((_, ri) => ri !== resIdx) } : m
    ));
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, {
      question_id: makeId('q'),
      question: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      points: 10,
    }]);
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const updateOption = (qIdx, optIdx, value) => {
    setQuestions(prev => prev.map((q, i) =>
      i === qIdx ? { ...q, options: q.options.map((o, oi) => oi === optIdx ? value : o) } : q
    ));
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Course title is required'); return; }
    if (!form.description.trim()) { toast.error('Course description is required'); return; }
    // Modules + exam only matter for online/hybrid; in-person courses ship without them.
    const isInPerson = form.delivery_mode === 'in-person';
    if (!isInPerson && modules.some(m => !m.title.trim())) { toast.error('All modules need a title'); return; }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        intro: (form.intro || '').trim(),
        description: form.description.trim(),
        level: form.level,
        topic_tags: form.topic_tags.split(',').map(t => t.trim()).filter(Boolean),
        price: Math.max(parseFloat(form.price) || 0, 0),
        max_participants: form.max_participants === '' || form.max_participants == null ? null : Number(form.max_participants),
        pass_mark: parseInt(form.pass_mark) || 70,
        cpd_credits: parseFloat(form.cpd_credits) || 0,
        delivery_mode: form.delivery_mode,
        coming_soon: form.coming_soon,
        trainer_name: form.trainer_name.trim(),
        venue: form.venue.trim(),
        course_dates: form.course_dates.trim(),
        sessions: (form.sessions || [])
          .filter(s => s.date)
          .map(s => ({
            session_id: s.session_id || makeId('sess'),
            date: s.date,
            start_time: (s.start_time || '').trim(),
            end_time: (s.end_time || '').trim(),
            venue: (s.venue || '').trim(),
            capacity: s.capacity === '' || s.capacity == null ? null : Number(s.capacity),
            notes: (s.notes || '').trim(),
          })),
        what_you_will_learn: (form.what_you_will_learn || '').trim(),
        // For in-person courses we intentionally clear modules + exam so the
        // public course detail page renders the in-person template without
        // any orphaned online learning content.
        modules: isInPerson ? [] : modules.map(m => ({
          module_id: m.module_id,
          title: m.title.trim(),
          duration: m.duration.trim(),
          content: m.content,
          video_url: m.video_url.trim(),
          resources: m.resources.filter(r => r.url.trim()),
        })),
        exam: !isInPerson && questions.length > 0 ? {
          questions: questions.map(q => ({
            question_id: q.question_id,
            question: q.question.trim(),
            options: q.options,
            correct_answer: q.correct_answer,
            points: q.points || 10,
          })),
          pass_mark: parseInt(form.pass_mark) || 70,
        } : null,
      };

      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing
        ? `${BACKEND_URL}/api/courses/${courseId}`
        : `${BACKEND_URL}/api/courses`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Read the body once as text — gracefully handle both JSON errors from
        // FastAPI and HTML error pages from the proxy (e.g. 413 Payload Too
        // Large, 502 Bad Gateway). Previously we always called res.json() which
        // threw "Unexpected token '<'" when the response was HTML.
        let raw = '';
        try { raw = await res.text(); } catch {}
        let detail = '';
        try { detail = JSON.parse(raw).detail; } catch {}
        if (!detail) {
          if (res.status === 413) detail = 'Course is too large to save. The content (likely pasted images) exceeds the upload limit. Try uploading images as URLs instead of pasting them in.';
          else if (res.status === 401 || res.status === 403) detail = 'You are not signed in or not an admin. Refresh and try again.';
          else if (res.status >= 500) detail = `Server error (${res.status}). Please try again or contact support.`;
          else detail = `Save failed (${res.status}).`;
        }
        throw new Error(detail);
      }

      toast.success(isEditing ? 'Course updated!' : 'Course created!');
      navigate('/admin');
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6">
            <button onClick={() => navigate('/admin')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-brand-navy mb-3 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Back to Admin
            </button>
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold font-heading gradient-text" data-testid="course-builder-title">
                {isEditing ? 'Edit Course' : 'Create Course'}
              </h1>
              <Button data-testid="save-course-btn" onClick={handleSave} disabled={saving}
                className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Course'}
              </Button>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList>
              <TabsTrigger value="details" data-testid="tab-details">
                <BookOpen className="h-4 w-4 mr-2" /> Details
              </TabsTrigger>
              {form.delivery_mode !== 'in-person' && (
                <>
                  <TabsTrigger value="modules" data-testid="tab-modules">
                    <FileText className="h-4 w-4 mr-2" /> Modules ({modules.length})
                  </TabsTrigger>
                  <TabsTrigger value="exam" data-testid="tab-exam">
                    <HelpCircle className="h-4 w-4 mr-2" /> Exam ({questions.length}q)
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4">
              <Card className="p-6 space-y-4">
                <div>
                  <Label>Course Title *</Label>
                  <Input data-testid="course-title-input" value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Net Zero Supply Chain Strategy" />
                </div>
                <div>
                  <Label>Intro</Label>
                  <RichTextEditor value={form.intro || ''}
                    onChange={v => setForm(f => ({ ...f, intro: v }))}
                    placeholder="A 1–2 sentence outline of the course — shown on the listing card."
                    testId="course-intro-input" minHeight="4rem" />
                  <p className="text-xs text-slate-400 mt-1">
                    Brief outline that appears on the course card. The full description below is shown inside the course page.
                  </p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <RichTextEditor value={form.description}
                    onChange={v => setForm(f => ({ ...f, description: v }))}
                    placeholder="A 2–3 paragraph overview of the course."
                    testId="course-desc-input" minHeight="8rem" />
                  <p className="text-xs text-slate-400 mt-1">Full description shown inside the course detail page once a member clicks the card.</p>
                </div>
                <div>
                  <Label>What you&rsquo;ll learn</Label>
                  <RichTextEditor value={form.what_you_will_learn || ''}
                    onChange={v => setForm(f => ({ ...f, what_you_will_learn: v }))}
                    placeholder="Use the bullet-list button to add one outcome per line."
                    testId="course-wyl-input" minHeight="7rem" />
                  <p className="text-xs text-slate-400 mt-1">Shown to members as a separate &ldquo;What you&rsquo;ll learn&rdquo; box. Leave blank to hide that section.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Level</Label>
                    <Input value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))}
                      placeholder="Beginner / Intermediate / Advanced" />
                  </div>
                  <div>
                    <Label>Price (£)</Label>
                    <Input data-testid="course-price-input" type="number" min="0" value={form.price}
                      onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
                    <p className="text-[11px] text-slate-400 mt-1">Set to 0 for a free course</p>
                  </div>
                  <div>
                    <Label>Max Participants</Label>
                    <Input data-testid="course-max-participants-input" type="number" min="1" value={form.max_participants}
                      placeholder="No limit"
                      onChange={e => setForm(f => ({ ...f, max_participants: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Topic Tags (comma-separated)</Label>
                    <Input value={form.topic_tags}
                      onChange={e => setForm(f => ({ ...f, topic_tags: e.target.value }))}
                      placeholder="Net Zero, Scope 3, Strategy" />
                  </div>
                  <div>
                    <Label>Exam Pass Mark (%)</Label>
                    <Input data-testid="pass-mark-input" type="number" min="0" max="100" value={form.pass_mark}
                      onChange={e => setForm(f => ({ ...f, pass_mark: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>CPD Credits</Label>
                  <Input data-testid="cpd-credits-input" type="number" min="0" step="0.5" value={form.cpd_credits}
                    onChange={e => setForm(f => ({ ...f, cpd_credits: e.target.value }))}
                    placeholder="e.g. 2.5" />
                  <p className="text-xs text-slate-400 mt-1">Number of CPD credits awarded on successful completion</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Delivery Mode</Label>
                    <Select value={form.delivery_mode} onValueChange={v => {
                      setForm(f => ({ ...f, delivery_mode: v }));
                      // Modules & Exam tabs disappear for in-person; bounce the
                      // admin back to Details if they're on a now-hidden tab.
                      if (v === 'in-person' && (activeTab === 'modules' || activeTab === 'exam')) {
                        setActiveTab('details');
                      }
                    }}>
                      <SelectTrigger data-testid="delivery-mode-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="online" data-testid="delivery-mode-online">Online</SelectItem>
                        <SelectItem value="in-person" data-testid="delivery-mode-in-person">In-Person</SelectItem>
                        <SelectItem value="hybrid" data-testid="delivery-mode-hybrid">Hybrid (Online + In-Person)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer pb-2" data-testid="coming-soon-toggle">
                      <input type="checkbox" checked={form.coming_soon}
                        onChange={e => setForm(f => ({ ...f, coming_soon: e.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-brand-navy focus:ring-brand-navy" />
                      <span className="text-sm font-medium text-slate-700">Coming Soon</span>
                    </label>
                  </div>
                </div>

                {/* Trainer / venue only for in-person or hybrid; session dates for every mode. */}
                {(form.delivery_mode === 'in-person' || form.delivery_mode === 'hybrid') && (
                  <div className="grid md:grid-cols-3 gap-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Trainer Name</Label>
                      <Input placeholder="e.g. Dr. Sarah Mitchell" value={form.trainer_name}
                        onChange={e => setForm(f => ({ ...f, trainer_name: e.target.value }))} data-testid="trainer-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Default Venue</Label>
                      <Input placeholder="e.g. London Convention Centre" value={form.venue}
                        onChange={e => setForm(f => ({ ...f, venue: e.target.value }))} data-testid="venue-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Summary Date(s)</Label>
                      <Input placeholder="Optional plain-text summary (e.g. 'Spring 2026')" value={form.course_dates}
                        onChange={e => setForm(f => ({ ...f, course_dates: e.target.value }))} data-testid="course-dates-input" />
                    </div>
                  </div>
                )}

                  {/* Session schedule — one course can run on several dates. */}
                  <div className="p-4 bg-emerald-50/40 rounded-lg border border-emerald-100" data-testid="course-sessions-section">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700">Available Session Dates</Label>
                        <p className="text-[11px] text-slate-500 mt-0.5">Add one row per date this course runs. Members will pick which date they want to attend at enrolment.</p>
                      </div>
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => setForm(f => ({ ...f, sessions: [...(f.sessions || []), { session_id: makeId('sess'), date: '', start_time: '', end_time: '', venue: '', capacity: '', notes: '' }] }))}
                        data-testid="add-course-session-btn">
                        <Plus className="h-3 w-3 mr-1" /> Add date
                      </Button>
                    </div>
                    {(form.sessions || []).length === 0 ? (
                      <p className="text-xs text-slate-400 italic">No dates yet. Add at least one if you want members to register for a specific session.</p>
                    ) : (
                      <div className="space-y-2">
                        {form.sessions.map((s, sIdx) => (
                          <div key={s.session_id || sIdx} className="grid grid-cols-12 gap-2 items-end p-2 bg-white rounded border border-slate-200" data-testid={`course-session-${sIdx}`}>
                            <div className="col-span-12 md:col-span-3">
                              <Label className="text-[10px] text-slate-500">Date *</Label>
                              <Input type="date" value={s.date}
                                onChange={e => { const arr = [...form.sessions]; arr[sIdx].date = e.target.value; setForm(f => ({ ...f, sessions: arr })); }}
                                data-testid={`session-date-${sIdx}`} className="text-sm" />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                              <Label className="text-[10px] text-slate-500">Start</Label>
                              <Input type="time" value={s.start_time}
                                onChange={e => { const arr = [...form.sessions]; arr[sIdx].start_time = e.target.value; setForm(f => ({ ...f, sessions: arr })); }}
                                className="text-sm" />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                              <Label className="text-[10px] text-slate-500">End</Label>
                              <Input type="time" value={s.end_time}
                                onChange={e => { const arr = [...form.sessions]; arr[sIdx].end_time = e.target.value; setForm(f => ({ ...f, sessions: arr })); }}
                                className="text-sm" />
                            </div>
                            <div className="col-span-9 md:col-span-3">
                              <Label className="text-[10px] text-slate-500">{form.delivery_mode === 'online' ? 'Joining link / location' : 'Venue (overrides default)'}</Label>
                              <Input placeholder={form.delivery_mode === 'online' ? 'e.g. Teams link' : (form.venue || 'Use default venue')} value={s.venue}
                                onChange={e => { const arr = [...form.sessions]; arr[sIdx].venue = e.target.value; setForm(f => ({ ...f, sessions: arr })); }}
                                className="text-sm" />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                              <Label className="text-[10px] text-slate-500">Cap</Label>
                              <Input type="number" min="0" value={s.capacity}
                                onChange={e => { const arr = [...form.sessions]; arr[sIdx].capacity = e.target.value; setForm(f => ({ ...f, sessions: arr })); }}
                                placeholder="∞" className="text-sm" />
                            </div>
                            <div className="col-span-1 md:col-span-1 flex justify-end">
                              <Button type="button" size="sm" variant="ghost" className="text-red-500 h-9 w-9 p-0"
                                onClick={() => { const arr = form.sessions.filter((_, i) => i !== sIdx); setForm(f => ({ ...f, sessions: arr })); }}
                                data-testid={`remove-session-${sIdx}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
              </Card>
            </TabsContent>

            {/* Modules Tab */}
            <TabsContent value="modules" className="space-y-4">
              {modules.map((mod, mi) => (
                <Card key={mod.module_id} className="p-5 space-y-4" data-testid={`module-editor-${mi}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-700">Module {mi + 1}</h3>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                      onClick={() => removeModule(mi)} data-testid={`remove-module-${mi}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2">
                      <Label>Module Title *</Label>
                      <Input value={mod.title} onChange={e => updateModule(mi, 'title', e.target.value)}
                        placeholder="e.g. Understanding Supply Chain Emissions" data-testid={`module-title-${mi}`} />
                    </div>
                    <div>
                      <Label>Duration</Label>
                      <Input value={mod.duration} onChange={e => updateModule(mi, 'duration', e.target.value)}
                        placeholder="e.g. 45 min" />
                    </div>
                  </div>

                  <div>
                    <Label><Video className="inline h-3.5 w-3.5 mr-1" />Video URL</Label>
                    <Input value={mod.video_url} onChange={e => updateModule(mi, 'video_url', e.target.value)}
                      placeholder="YouTube or video URL" data-testid={`module-video-${mi}`} />
                  </div>

                  <div>
                    <Label><FileText className="inline h-3.5 w-3.5 mr-1" />Study Content</Label>
                    <Textarea value={mod.content} onChange={e => updateModule(mi, 'content', e.target.value)}
                      placeholder="Write the study guide content for this module..."
                      className="min-h-32" data-testid={`module-content-${mi}`} />
                  </div>

                  {/* Resources */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label><LinkIcon className="inline h-3.5 w-3.5 mr-1" />Resources & Links</Label>
                      <Button variant="ghost" size="sm" onClick={() => addResource(mi)}
                        className="text-brand-navy" data-testid={`add-resource-${mi}`}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add Link
                      </Button>
                    </div>
                    {mod.resources.map((r, ri) => (
                      <div key={ri} className="flex gap-2 mb-2">
                        <Input value={r.title} onChange={e => updateResource(mi, ri, 'title', e.target.value)}
                          placeholder="Link title" className="flex-1" />
                        <Input value={r.url} onChange={e => updateResource(mi, ri, 'url', e.target.value)}
                          placeholder="https://..." className="flex-[2]" />
                        <Button variant="ghost" size="sm" onClick={() => removeResource(mi, ri)} className="text-red-500">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}

              <Button variant="outline" onClick={addModule} className="w-full border-dashed" data-testid="add-module-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Module
              </Button>
            </TabsContent>

            {/* Exam Tab */}
            <TabsContent value="exam" className="space-y-4">
              <Card className="p-4 bg-brand-navy/5 border-brand-navy/10">
                <p className="text-sm text-slate-600">
                  Create multiple-choice questions for the course exam. Students must score at least <strong>{form.pass_mark}%</strong> to pass and receive a certificate.
                </p>
              </Card>

              {questions.map((q, qi) => (
                <Card key={q.question_id} className="p-5 space-y-4" data-testid={`question-editor-${qi}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-700">Question {qi + 1}</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <Label className="text-xs">Points:</Label>
                        <Input type="number" min="1" value={q.points} className="w-16 h-8 text-sm"
                          onChange={e => updateQuestion(qi, 'points', parseInt(e.target.value) || 10)}
                          data-testid={`q-points-${qi}`} />
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                        onClick={() => removeQuestion(qi)} data-testid={`remove-question-${qi}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>Question *</Label>
                    <Textarea value={q.question} onChange={e => updateQuestion(qi, 'question', e.target.value)}
                      placeholder="Enter the question..." className="min-h-16" data-testid={`q-text-${qi}`} />
                  </div>

                  <div className="space-y-2">
                    <Label>Answer Options (click the circle to mark the correct answer)</Label>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <button type="button"
                          onClick={() => updateQuestion(qi, 'correct_answer', oi)}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                            q.correct_answer === oi
                              ? 'border-green-500 bg-green-500'
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                          data-testid={`correct-${qi}-${oi}`}>
                          {q.correct_answer === oi && <CheckCircle className="h-4 w-4 text-white" />}
                        </button>
                        <Input value={opt} onChange={e => updateOption(qi, oi, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                          className={q.correct_answer === oi ? 'border-green-300 bg-green-50' : ''}
                          data-testid={`option-${qi}-${oi}`} />
                      </div>
                    ))}
                  </div>
                </Card>
              ))}

              <Button variant="outline" onClick={addQuestion} className="w-full border-dashed" data-testid="add-question-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Question
              </Button>

              {questions.length > 0 && (
                <Card className="p-4 text-sm text-slate-600">
                  Total: {questions.length} questions, {questions.reduce((s, q) => s + (q.points || 10), 0)} points.
                  Pass mark: {form.pass_mark}% ({Math.ceil(questions.reduce((s, q) => s + (q.points || 10), 0) * (parseInt(form.pass_mark) || 70) / 100)} points needed)
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
